import {
    Component,
    EventEmitter,
    Input,
    Output,
    OnInit,
    forwardRef,
    HostBinding,
    OnChanges,
    SimpleChanges,
    OnDestroy,
} from "@angular/core";
import { Router } from "@angular/router";
import { DependentChoice } from "../../model/dependentchoice";
import { Observable, Observer, Subscription } from "rxjs";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { NavigationHelper } from "../../services/navigation.helper";
import { Labeler } from "../../model/labeler";

@Component({
    selector: "dependent-choice",
    templateUrl: "./dependent-choice.component.html",
    styleUrls: ["./dependent-choice.component.css"],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DependentChoiceComponent),
            multi: true,
        },
    ],
})
export class DependentChoiceComponent implements OnInit, OnChanges, OnDestroy {
    @Output() dependentEvent: EventEmitter<DependentChoice> = new EventEmitter();
    @Output() dependentSelected: EventEmitter<number> = new EventEmitter();
    @Input() topic: string;
    @Input() label!: string;
    @Input() ticketId: number;
    @Input() icon!: string;
    @Input() choiceObserver: Partial<Observer<number>>;
    @Input() labeler: Observable<string>;
    @Input() labelOnDemand: Labeler;
    @Input() detailsRoute!: string;
    @Input() selectedId: any;
    @Input() clickableLink: boolean = false;
    @Input() reduced: boolean = false;
    @Input() showLabel: boolean = true;
    @Input() isTicketContext: boolean = false;

    selectedLabel: string = "";
    status: number = 0;
    disabled: boolean = false;
    selectedChoice: DependentChoice;
    selectedBackgroundColor: string = "#ccc";
    selectedColor: string = "#555";
    selectedImportance: number = 2;

    private subscriptions: Subscription[] = [];

    @HostBinding("class.reduced") get isReduced() {
        return this.reduced;
    }

    @HostBinding("style.--choice-color") get cssChoiceColor() {
        return this.selectedColor;
    }

    constructor(private router: Router, private nav: NavigationHelper) { }

    ngOnInit(): void {
        if (!this.icon) {
            switch (this.topic) {
                case "status":
                    this.icon = "check_circle";
                    break;
                case "priority":
                    this.icon = "flag";
                    break;
                case "branch":
                case "branches":
                    this.icon = "business";
                    break;
                case "department":
                case "departments":
                    this.icon = "apartment";
                    break;
                case "assigned":
                    this.icon = "person";
                    break;
                default:
                    this.icon = "help_outline";
            }
        }

        this.updateSelected(this.selectedId);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["selectedId"]) {
            this.updateSelected(changes["selectedId"].currentValue);
        }
    }

    private updateSelected(id: number): void {
        this.selectedId = id;

        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.subscriptions = [];

        if (this.labeler) {
            const sub = this.labeler.subscribe((label) => {
                this.selectedLabel = label;
            });
            this.subscriptions.push(sub);
        } else if (this.labelOnDemand) {
            const subLabel = this.labelOnDemand.makeLabel(id).subscribe((l) => {
                if (l && l.trim().length > 0) {
                    this.selectedLabel = l;
                }
            });
            const subColor = this.labelOnDemand.makeColor(id).subscribe((c) => {
                if (c && c.trim().length > 0) {
                    this.selectedColor = c;
                }
            });
            const subImportance = this.labelOnDemand
                .makeImportance(id)
                .subscribe((importance) => {
                    this.selectedImportance = importance;
                });
            const subBackground = this.labelOnDemand
                .makeBackgroundColor(id)
                .subscribe((c) => {
                    if (c && c.trim().length > 0) {
                        this.selectedBackgroundColor = c;
                    }
                });

            this.subscriptions.push(subLabel, subColor, subImportance, subBackground);
        } else {
            this.selectedLabel = this.label;
        }
    }

    onDependentClick(): void {
        if (this.clickableLink) {
            this.router.navigate([`${this.topic}/${this.selectedId}`]).then(() => {
                this.nav.refresh();
            });
        }
    }

    onChange = (id: number) => { };

    onTouched = () => { };

    writeValue(id: number): void {
        this.selectedId = id;
        this.onChange(this.selectedId);
    }

    registerOnChange(fn: (id: number) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    ngOnDestroy() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
}
